import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Lock } from "lucide-react";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { MEMBERSHIP_PRICE, VAT_SUFFIX } from "@/data/packages";

const monthLabel = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

const groupByMonth = (items) => {
  const groups = [];
  for (const it of items) {
    const key = monthLabel(it.approved_at || it.published);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(it);
    else groups.push({ key, items: [it] });
  }
  return groups;
};

export default function LawChanges() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/horizon/public").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);

  return (
    <main data-testid="law-changes-page">
      <Seo
        title="UK Employment Law Changes This Month | Electra HR"
        siteName="Electra HR"
        description="New employment legislation, tribunal appeal decisions and official guidance for UK employers, collected from official sources and checked by a solicitor."
      />
      <section className="grain bg-ink pb-16 pt-32 sm:pt-40 text-paper">
        <div className="container-x">
          <p className="eyebrow text-pink">Horizon scan</p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            Employment law changes, as they happen.
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/65">
            New legislation, Employment Appeal Tribunal decisions and official guidance that matter to UK employers.
            Collected from official sources and checked by a solicitor before anything appears here.
          </p>
        </div>
      </section>

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            {items === null ? (
              <p className="text-ink/50">Loading…</p>
            ) : items.length === 0 ? (
              <p data-testid="law-changes-empty" className="text-base leading-relaxed text-ink/70">
                The first round-up is being checked now. In the meantime, our{" "}
                <Link to="/blog" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">briefings</Link>{" "}
                and <Link to="/calculators" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">free calculators</Link>{" "}
                cover the rules in force today.
              </p>
            ) : (
              groupByMonth(items).map((g) => (
                <div key={g.key} className="mb-12">
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">{g.key}</h2>
                  <ul className="mt-6 divide-y divide-ink/10 border-y border-ink/10">
                    {g.items.map((it) => (
                      <li key={it.id} data-testid="law-change-item" className="py-5">
                        <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink/45">{it.source}</p>
                        <a href={it.url} target="_blank" rel="noopener noreferrer" className="group mt-1.5 inline-flex items-start gap-2 font-display text-lg font-semibold leading-snug text-ink hover:text-pink">
                          {it.title} <ExternalLink size={15} className="mt-1.5 shrink-0 opacity-50 group-hover:opacity-100" />
                        </a>
                        <p className="mt-2 flex items-center gap-2 text-sm text-ink/45">
                          <Lock size={13} /> What it means for you and the dates to act by: members only
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>

          <aside className="h-fit border-2 border-ink bg-ink p-7 text-paper lg:sticky lg:top-28">
            <p className="eyebrow text-pink">Members</p>
            <p className="mt-4 font-display text-xl font-semibold">Know what each change means for you, and when to act.</p>
            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-paper/70">
              <li>A plain-English note on every item</li>
              <li>The date you need to act by, where there is one</li>
              <li>A monthly email round-up</li>
              <li>Every item checked by a solicitor</li>
            </ul>
            <p className="mt-5 font-display text-2xl font-semibold text-pink">£{MEMBERSHIP_PRICE} a month{VAT_SUFFIX}</p>
            <p className="mt-1 text-sm text-paper/60">Cancel any time. Free for 12 months with a block of 10 hours or more.</p>
            <Link
              to="/contact?need=membership"
              data-testid="law-changes-join"
              className="group mt-6 inline-flex items-center gap-2 bg-pink px-5 py-3 font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5"
            >
              Ask about membership <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <p className="mt-4 text-xs leading-relaxed text-paper/45">
              General information, not advice on your situation. Need help applying a change? Use{" "}
              <Link to="/services#pricing" className="underline">your hours</Link>.
            </p>
          </aside>
        </div>
      </section>
    </main>
  );
}
